import setup from '../assets/data/setup.json';
import type { ResumeVariant } from './types';
import { getPrimaryTechnologies } from './categorizeSkills';

export function generateProfessionalSummary(variant: ResumeVariant): string {
  if (setup.resumeConfig?.summary) {
    return setup.resumeConfig.summary;
  }

  const years = setup.stats.yearsExperience;
  const technologies = getPrimaryTechnologies(variant.keywords);
  const about = setup.about as { story?: string; domains?: string[] };
  const domains =
    about.domains ??
    setup.whatIBuild.slice(0, 4).map((item) => item.title.toLowerCase());
  const industries = 'US-based enterprise and SaaS companies';
  const systems = domains.slice(0, 4).join(', ');
  const cloud = setup.skills.Cloud?.slice(0, 3).join(', ') || 'AWS';
  const automation = setup.skills.Automation?.slice(0, 3).join(', ') || 'workflow automation';
  const ai = setup.skills.AI?.slice(0, 2).join(' and ') || 'AI-powered applications';
  const mindset =
    'problem-first engineering, rapid technology adoption, and end-to-end ownership from architecture through production';

  return [
    `${variant.title} with ${years}+ years of experience delivering production software for ${industries}.`,
    `Specializes in ${systems}, with hands-on expertise in ${technologies.join(', ')}.`,
    `Experienced in cloud infrastructure (${cloud}), ${automation}, and ${ai}.`,
    `Known for ${mindset}.`,
  ].join(' ');
}

export function generateResumeHighlights(): string[] {
  const highlights = new Set<string>();

  setup.whatIBuild.forEach((item) => {
    highlights.add(item.title);
  });

  setup.engineeringHighlights.forEach((item) => {
    highlights.add(item.title);
  });

  setup.integrations.slice(0, 8).forEach((integration) => {
    highlights.add(`${integration.name} Integration`);
  });

  const keywordHighlights = [
    { test: () => setup.experiences.some((e) => e.highlight.toLowerCase().includes('erp')), label: 'Enterprise ERP Systems' },
    { test: () => setup.experiences.some((e) => e.highlight.toLowerCase().includes('crm')), label: 'CRM Platforms' },
    { test: () => setup.projects.some((p) => p.tags?.includes('saas')), label: 'Enterprise SaaS' },
    { test: () => setup.skills.Cloud?.includes('CI/CD'), label: 'CI/CD Pipelines' },
    { test: () => setup.skills.Cloud?.includes('AWS'), label: 'AWS Cloud Infrastructure' },
    { test: () => setup.skills.AI?.length, label: 'AI Applications' },
    { test: () => setup.integrations.some((i) => i.name === 'QuickBooks'), label: 'QuickBooks Integrations' },
    { test: () => setup.integrations.some((i) => i.name.includes('Google')), label: 'Google APIs' },
    { test: () => true, label: 'Performance Optimization' },
    { test: () => true, label: 'Security & Reliability' },
  ];

  keywordHighlights.forEach(({ test, label }) => {
    if (test()) highlights.add(label);
  });

  return Array.from(highlights).slice(0, 12);
}
